/**
 * useWorkspaceAISettings Hook
 * Manages Workspace AI Keys and Default Models via REST API.
 * Model data from Registry API: GET /api/workspace/:wsId/ai/registry/models.
 */

import { notify } from '@affine/component';
import type {
  AIModality,
  AIProvider,
  ModelOption,
} from '@affine/core/components/ai-settings';
import { PROVIDERS } from '@affine/core/components/ai-settings';
import {
  useRegistryModels,
  useRegistryProviders,
} from '@affine/core/components/ai-settings/use-ai-registry';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

// Types
export interface WorkspaceKey {
  provider: AIProvider;
  maskedKey: string | null;
  isSet: boolean;
}

export interface WorkspaceDefaults {
  chat: string | null;
  image: string | null;
  stt: string | null;
  rowMatching: string | null;
  webSearch: string | null;
  embedding: string | null;
}

// sl-amc0.4: Chunking configuration
export interface ChunkingConfig {
  maxChunksPerDoc: number;
  chunkSize: number;
}

// sl-sf9o: Model routing configuration
export interface RoutingConfig {
  routingMode: string; // 'off' | 'auto'
  budgetAwareDowngrade: boolean;
}

export type PdfOcrPolicy =
  | 'manual'
  | 'auto_under_limit'
  | 'always_auto_within_limits'
  | 'disabled_visible'
  | 'disabled_hidden';

export interface PdfOcrPolicyConfig {
  policy: PdfOcrPolicy;
  maxCandidatePages: number;
  maxCreditsPerPdf: number;
  maxProviderCalls: number;
  confirmationThresholdPages: number;
}

interface UseWorkspaceAISettingsReturn {
  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Keys enabled toggle
  keysEnabled: boolean;
  setKeysEnabled: (enabled: boolean) => Promise<void>;

  // Keys
  keys: Map<AIProvider, WorkspaceKey>;
  saveKey: (provider: AIProvider, key: string) => Promise<void>;
  removeKey: (provider: AIProvider) => Promise<void>;

  // Default Models
  defaults: WorkspaceDefaults;
  setDefault: (modality: AIModality, model: string | null) => Promise<void>;

  // sl-amc0.4: Chunking configuration
  chunkingConfig: ChunkingConfig;
  setChunkingConfig: (config: Partial<ChunkingConfig>) => Promise<void>;

  // sl-sf9o: Model routing configuration
  routingConfig: RoutingConfig;
  setRoutingConfig: (config: Partial<RoutingConfig>) => Promise<void>;

  pdfOcrPolicyConfig: PdfOcrPolicyConfig;
  setPdfOcrPolicyConfig: (config: Partial<PdfOcrPolicyConfig>) => Promise<void>;

  // sl-amc0.4: Registry models for pricing lookup
  registryModels: import('@affine/core/components/ai-settings/use-ai-registry').RegistryModel[];

  // Available models based on keys
  getAvailableModels: (modality: AIModality) => ModelOption[];

  // Refresh
  refresh: () => Promise<void>;
}

// sl-y41p: Map frontend field names to backend field names
const MODALITY_TO_BACKEND_FIELD: Record<keyof WorkspaceDefaults, string> = {
  chat: 'chatModel',
  image: 'imageModel',
  stt: 'sttModel',
  rowMatching: 'rowMatchingModel',
  webSearch: 'webSearchModel',
  embedding: 'embeddingModel',
};

// sl-y41p: Parse backend response to frontend format
function parseBackendDefaults(
  backendDefaults: Record<string, unknown> | null
): WorkspaceDefaults {
  if (!backendDefaults) {
    return {
      chat: null,
      image: null,
      stt: null,
      rowMatching: null,
      webSearch: null,
      embedding: null,
    };
  }
  return {
    chat: (backendDefaults.chatModel as string) ?? null,
    image: (backendDefaults.imageModel as string) ?? null,
    stt: (backendDefaults.sttModel as string) ?? null,
    rowMatching: (backendDefaults.rowMatchingModel as string) ?? null,
    webSearch: (backendDefaults.webSearchModel as string) ?? null,
    embedding: (backendDefaults.embeddingModel as string) ?? null,
  };
}

// sl-y41p: Convert frontend defaults to backend format
function toBackendFormat(
  frontendDefaults: Partial<WorkspaceDefaults>
): Record<string, string | null> {
  const result: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(frontendDefaults)) {
    const backendKey =
      MODALITY_TO_BACKEND_FIELD[key as keyof WorkspaceDefaults];
    if (backendKey) {
      result[backendKey] = value;
    }
  }
  return result;
}

// sl-amc0.4: Default chunking values (must match backend defaults)
const DEFAULT_CHUNKING: ChunkingConfig = {
  maxChunksPerDoc: 100,
  chunkSize: 1200,
};

// sl-sf9o: Default routing config (must match backend defaults)
const DEFAULT_ROUTING: RoutingConfig = {
  routingMode: 'off',
  budgetAwareDowngrade: false,
};

const DEFAULT_PDF_OCR_POLICY: PdfOcrPolicyConfig = {
  policy: 'manual',
  maxCandidatePages: 48,
  maxCreditsPerPdf: 50,
  maxProviderCalls: 4,
  confirmationThresholdPages: 12,
};

export function useWorkspaceAISettings(): UseWorkspaceAISettingsReturn {
  const workspace = useService(WorkspaceService).workspace;
  const workspaceId = workspace.id;
  const requestWorkspaceAI = useCallback(
    (
      path: string,
      init?: RequestInit,
      mode: 'background' | 'foreground' = 'background'
    ) =>
      defaultWorkspaceApiClient.request({
        workspaceId,
        path: `/api/workspace/${encodeURIComponent(workspaceId)}${path}`,
        init,
        category: 'settings',
        key: `${init?.method ?? 'GET'}:${path}`,
        mode,
        priority: mode === 'foreground' ? 'foreground' : 'visible',
      }),
    [workspaceId]
  );

  // sl-kpg5.21: Fetch models + providers from Registry API
  const {
    models: registryModels,
    modelsByProvider,
    modalityModelIds,
    isLoading: isRegistryLoading,
    refetch: refetchModels,
  } = useRegistryModels(workspaceId);
  const { providerNameMap, refetch: refetchProviders } =
    useRegistryProviders(workspaceId);

  const [isKeysLoading, setIsKeysLoading] = useState(true);
  // Combined: true while EITHER keys/defaults OR registry models are loading
  const isLoading = isKeysLoading || isRegistryLoading;
  const [isSaving, setIsSaving] = useState(false);
  const [keysEnabled, setKeysEnabledState] = useState(true);
  const [keys, setKeys] = useState<Map<AIProvider, WorkspaceKey>>(new Map());
  const [defaults, setDefaults] = useState<WorkspaceDefaults>({
    chat: null,
    image: null,
    stt: null,
    rowMatching: null,
    webSearch: null,
    embedding: null,
  });
  // sl-amc0.4: Chunking config state
  const [chunkingConfig, setChunkingConfigState] =
    useState<ChunkingConfig>(DEFAULT_CHUNKING);
  // sl-sf9o: Routing config state
  const [routingConfig, setRoutingConfigState] =
    useState<RoutingConfig>(DEFAULT_ROUTING);
  const [pdfOcrPolicyConfig, setPdfOcrPolicyConfigState] =
    useState<PdfOcrPolicyConfig>(DEFAULT_PDF_OCR_POLICY);

  // Fetch keys + defaults (non-registry data)
  const fetchKeysAndDefaults = useCallback(async () => {
    setIsKeysLoading(true);
    try {
      const [keysRes, defaultsRes] = await Promise.all([
        requestWorkspaceAI('/ai-keys'),
        requestWorkspaceAI('/ai-defaults'),
      ]);

      if (keysRes.ok) {
        const keysData = await keysRes.json();
        const newKeys = new Map<AIProvider, WorkspaceKey>();

        // Initialize all providers from static PROVIDERS (UI metadata source)
        for (const provider of PROVIDERS) {
          const keyData = keysData.keys?.find(
            (k: { provider: string }) => k.provider === provider.id
          );
          newKeys.set(provider.id, {
            provider: provider.id,
            maskedKey: keyData?.maskedKey ?? null,
            isSet: keyData?.isSet ?? false,
          });
        }
        setKeys(newKeys);
      }

      if (defaultsRes.ok) {
        const defaultsData = await defaultsRes.json();
        // sl-y41p: Parse backend format to frontend format
        setDefaults(parseBackendDefaults(defaultsData.defaults));
        // Extract keysEnabled from defaults (default: true)
        setKeysEnabledState(defaultsData.defaults?.keysEnabled ?? true);
        // sl-amc0.4: Parse chunking config
        setChunkingConfigState({
          maxChunksPerDoc:
            defaultsData.defaults?.maxChunksPerDoc ??
            DEFAULT_CHUNKING.maxChunksPerDoc,
          chunkSize:
            defaultsData.defaults?.chunkSize ?? DEFAULT_CHUNKING.chunkSize,
        });
        // sl-sf9o: Parse routing config
        setRoutingConfigState({
          routingMode:
            defaultsData.defaults?.routingMode ?? DEFAULT_ROUTING.routingMode,
          budgetAwareDowngrade:
            defaultsData.defaults?.budgetAwareDowngrade ??
            DEFAULT_ROUTING.budgetAwareDowngrade,
        });
        setPdfOcrPolicyConfigState({
          policy:
            defaultsData.defaults?.pdfOcrPolicy ??
            DEFAULT_PDF_OCR_POLICY.policy,
          maxCandidatePages:
            defaultsData.defaults?.pdfOcrMaxCandidatePages ??
            DEFAULT_PDF_OCR_POLICY.maxCandidatePages,
          maxCreditsPerPdf:
            defaultsData.defaults?.pdfOcrMaxCreditsPerPdf ??
            DEFAULT_PDF_OCR_POLICY.maxCreditsPerPdf,
          maxProviderCalls:
            defaultsData.defaults?.pdfOcrMaxProviderCalls ??
            DEFAULT_PDF_OCR_POLICY.maxProviderCalls,
          confirmationThresholdPages:
            defaultsData.defaults?.pdfOcrConfirmationThresholdPages ??
            DEFAULT_PDF_OCR_POLICY.confirmationThresholdPages,
        });
      }
    } catch (error) {
      console.error('Failed to fetch workspace AI settings:', error);
      notify.error({ title: 'Failed to load AI settings' });
    } finally {
      setIsKeysLoading(false);
    }
  }, [requestWorkspaceAI]);

  // Initial load
  useEffect(() => {
    fetchKeysAndDefaults().catch(console.error);
  }, [fetchKeysAndDefaults]);

  // Combined refresh (keys + defaults + registry)
  const refresh = useCallback(async () => {
    await Promise.all([
      fetchKeysAndDefaults(),
      refetchModels(),
      refetchProviders(),
    ]);
  }, [fetchKeysAndDefaults, refetchModels, refetchProviders]);

  // Toggle keysEnabled
  const setKeysEnabled = useCallback(
    async (enabled: boolean) => {
      setIsSaving(true);
      try {
        const res = await requestWorkspaceAI(
          '/ai-defaults',
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ keysEnabled: enabled }),
          },
          'foreground'
        );

        if (!res.ok) throw new Error('Failed to update keys enabled');

        setKeysEnabledState(enabled);
        notify.success({
          title: enabled
            ? 'Workspace API keys enabled'
            : 'Workspace API keys disabled',
        });
      } catch (error) {
        console.error('Failed to toggle keys enabled:', error);
        notify.error({ title: 'Failed to update setting' });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [requestWorkspaceAI]
  );

  // Save key with auto-selection of default models
  const saveKey = useCallback(
    async (provider: AIProvider, key: string) => {
      setIsSaving(true);
      try {
        const res = await requestWorkspaceAI(
          `/ai-keys/${encodeURIComponent(provider)}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ apiKey: key }),
          },
          'foreground'
        );

        if (!res.ok) throw new Error('Failed to save key');

        // sl-y41p: Fetch fresh defaults BEFORE auto-selection to avoid stale closure
        const defaultsRes = await requestWorkspaceAI('/ai-defaults');
        const currentDefaults: WorkspaceDefaults = defaultsRes.ok
          ? parseBackendDefaults((await defaultsRes.json()).defaults)
          : {
              chat: null,
              image: null,
              stt: null,
              rowMatching: null,
              webSearch: null,
              embedding: null,
            };

        // sl-kpg5.21: Auto-select models using Registry API data
        const providerConfig = PROVIDERS.find(p => p.id === provider);
        if (providerConfig) {
          const providerModels = modelsByProvider[provider] || [];
          const autoSelections: Partial<WorkspaceDefaults> = {};

          for (const modality of providerConfig.modalities) {
            // Skip if already has a default (using FRESH data)
            if (currentDefaults[modality as keyof WorkspaceDefaults]) {
              continue;
            }

            // Find the first model from this provider for this modality
            const capabilityModels = modalityModelIds[modality] || [];
            const firstModel = providerModels.find(m =>
              capabilityModels.includes(m.id)
            );

            if (firstModel) {
              autoSelections[modality as keyof WorkspaceDefaults] =
                firstModel.id;
            }
          }

          // Save auto-selections if any
          if (Object.keys(autoSelections).length > 0) {
            // sl-y41p: Convert to backend format before sending
            const backendPayload = toBackendFormat({
              ...currentDefaults,
              ...autoSelections,
            });

            const saveRes = await requestWorkspaceAI(
              '/ai-defaults',
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(backendPayload),
              },
              'foreground'
            );

            if (saveRes.ok) {
              setDefaults(prev => ({
                ...prev,
                ...autoSelections,
              }));
            }
          }
        }

        // Refresh to get masked key and update all state
        await refresh();

        notify.success({ title: 'API key saved' });
      } catch (error) {
        console.error('Failed to save key:', error);
        notify.error({ title: 'Failed to save API key' });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [requestWorkspaceAI, refresh, modelsByProvider, modalityModelIds]
  );

  // Remove key
  const removeKey = useCallback(
    async (provider: AIProvider) => {
      setIsSaving(true);
      try {
        const res = await requestWorkspaceAI(
          `/ai-keys/${encodeURIComponent(provider)}`,
          { method: 'DELETE' },
          'foreground'
        );

        if (!res.ok) throw new Error('Failed to remove key');

        // Update local state
        setKeys(prev => {
          const next = new Map(prev);
          next.set(provider, {
            provider,
            maskedKey: null,
            isSet: false,
          });
          return next;
        });
        notify.success({ title: 'API key removed' });
      } catch (error) {
        console.error('Failed to remove key:', error);
        notify.error({ title: 'Failed to remove API key' });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [requestWorkspaceAI]
  );

  // Set default model
  const setDefault = useCallback(
    async (modality: AIModality, model: string | null) => {
      setIsSaving(true);
      try {
        // sl-y41p: Convert to backend format
        const backendField =
          MODALITY_TO_BACKEND_FIELD[modality as keyof WorkspaceDefaults];
        if (!backendField) {
          throw new Error(`Unknown modality: ${modality}`);
        }

        const res = await requestWorkspaceAI(
          '/ai-defaults',
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              [backendField]: model,
            }),
          },
          'foreground'
        );

        if (!res.ok) throw new Error('Failed to set default model');

        setDefaults(prev => ({
          ...prev,
          [modality]: model,
        }));

        // Notify chat sidebar to re-sync effective model
        if (modality === 'chat') {
          window.dispatchEvent(new CustomEvent('sl:ai-model-changed'));
        }
      } catch (error) {
        console.error('Failed to set default model:', error);
        notify.error({ title: 'Failed to set default model' });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [requestWorkspaceAI]
  );

  const setPdfOcrPolicyConfig = useCallback(
    async (config: Partial<PdfOcrPolicyConfig>) => {
      const next = { ...pdfOcrPolicyConfig, ...config };
      setIsSaving(true);
      try {
        const res = await requestWorkspaceAI(
          '/ai-defaults',
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pdfOcrPolicy: next.policy,
              pdfOcrMaxCandidatePages: next.maxCandidatePages,
              pdfOcrMaxCreditsPerPdf: next.maxCreditsPerPdf,
              pdfOcrMaxProviderCalls: next.maxProviderCalls,
              pdfOcrConfirmationThresholdPages: next.confirmationThresholdPages,
            }),
          },
          'foreground'
        );

        if (!res.ok) throw new Error('Failed to update PDF OCR policy');

        setPdfOcrPolicyConfigState(next);
        notify.success({ title: 'PDF image text policy saved' });
      } catch (error) {
        console.error('Failed to update PDF OCR policy:', error);
        notify.error({ title: 'Failed to update PDF image text policy' });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [requestWorkspaceAI, pdfOcrPolicyConfig]
  );

  // Get available models for a modality based on which keys are set
  const getAvailableModels = useCallback(
    (modality: AIModality): ModelOption[] => {
      const providers = PROVIDERS.filter(p => p.modalities.includes(modality));
      const availableModels: ModelOption[] = [];

      for (const provider of providers) {
        const keyInfo = keys.get(provider.id);
        if (keyInfo?.isSet) {
          const providerModels = modelsByProvider[provider.id] || [];
          const capabilityModels = modalityModelIds[modality] || [];
          const filtered = providerModels
            .filter(m => capabilityModels.includes(m.id))
            .map(m => ({
              ...m,
              providerName: providerNameMap.get(provider.id) ?? provider.name,
            }));
          availableModels.push(...filtered);
        }
      }

      return availableModels;
    },
    [keys, modelsByProvider, modalityModelIds, providerNameMap]
  );

  // sl-amc0.4: Save chunking config to backend
  const setChunkingConfig = useCallback(
    async (config: Partial<ChunkingConfig>) => {
      setIsSaving(true);
      try {
        const res = await requestWorkspaceAI(
          '/ai-defaults',
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(config),
          },
          'foreground'
        );

        if (!res.ok) throw new Error('Failed to update chunking config');

        setChunkingConfigState(prev => ({ ...prev, ...config }));
      } catch (error) {
        console.error('Failed to save chunking config:', error);
        notify.error({ title: 'Failed to save embedding configuration' });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [requestWorkspaceAI]
  );

  // sl-sf9o: Save routing config to backend
  const setRoutingConfig = useCallback(
    async (config: Partial<RoutingConfig>) => {
      setIsSaving(true);
      try {
        const res = await requestWorkspaceAI(
          '/ai-defaults',
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(config),
          },
          'foreground'
        );

        if (!res.ok) throw new Error('Failed to update routing config');

        setRoutingConfigState(prev => ({ ...prev, ...config }));
        // sl-sf9o: Notify other tabs/components to re-sync routing state
        window.dispatchEvent(new CustomEvent('sl:ai-model-changed'));
      } catch (error) {
        console.error('Failed to save routing config:', error);
        notify.error({ title: 'Failed to save routing configuration' });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [requestWorkspaceAI]
  );

  return {
    isLoading,
    isSaving,
    keysEnabled,
    setKeysEnabled,
    keys,
    saveKey,
    removeKey,
    defaults,
    setDefault,
    chunkingConfig,
    setChunkingConfig,
    routingConfig,
    setRoutingConfig,
    pdfOcrPolicyConfig,
    setPdfOcrPolicyConfig,
    registryModels,
    getAvailableModels,
    refresh,
  };
}
